<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import {
  NXGDSButton,
  NXGDSButtonSizes,
  NXGDSButtonVariants,
  NXGDSModal,
} from '@nsftx/seven-nxg-design-system'
import { NXGDSTagIndicator } from '@nsftx/seven-nxg-design-system'
import { useCalculatorStore } from '@/stores/calculator/calculatorStore'

const { t } = useI18n({ useScope: 'global' })
const calculatorStore = useCalculatorStore()
const { closeResetModal, confirmReset } = calculatorStore
const { isResetModalOpen, currency } = storeToRefs(calculatorStore)

const resetConfirmLabel = computed(() =>
  t('calculator_clear_session_currency', { currency: currency.value }),
)
</script>

<template>
  <NXGDSModal v-model="isResetModalOpen" :show-close-button="false" :close-on-outside-click="false">
    <template #default>
      <div class="ss-calculator-reset-modal">
        <i class="n-i n-i-calculator calculator-icon" />
      </div>

      <h3 class="ss-calculator-reset-modal__title">{{ t('calculator_reset_session_title') }}</h3>
      <p class="ss-calculator-reset-modal__message">{{ t('calculator_reset_session_message') }}</p>
    </template>

    <template #footer>
      <div class="ss-calculator-reset-modal__footer">
        <NXGDSButton
          width="100%"
          :variant="NXGDSButtonVariants.secondary"
          :size="NXGDSButtonSizes.medium"
          @click="closeResetModal"
        >
          <span>{{ t('common_cancel') }}</span>
          <NXGDSTagIndicator label="1" variant="subtle" />
        </NXGDSButton>
        <NXGDSButton
          width="100%"
          :variant="NXGDSButtonVariants.primary"
          :size="NXGDSButtonSizes.medium"
          @click="confirmReset"
        >
          <span>{{ resetConfirmLabel }}</span>
          <NXGDSTagIndicator label="2" variant="subtle" />
        </NXGDSButton>
      </div>
    </template>
  </NXGDSModal>
</template>

<style scoped lang="scss">
@use '@/styles/mixins' as mixins;
.ss-calculator-reset-modal {
  display: flex;
  height: 112px;
  padding: var(--nxgds-alias-spacing-lg);
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: var(--nxgds-alias-radius-corner-md);
  background: var(--nxgds-mapped-color-surface-component-subsection);
  margin-top: calc(-1 * var(--nxgds-alias-spacing-lg));

  .calculator-icon {
    font-size: var(--nxgds-core-type-size-4xl);
    line-height: var(--nxgds-core-type-line-height-3xl);
  }

  &__title {
    color: var(--nxgds-mapped-color-text-primary);
    @include mixins.typography('h3-medium');
    margin-bottom: var(--nxgds-alias-spacing-md);
    margin-top: var(--nxgds-alias-spacing-lg);
  }

  &__message {
    color: var(--nxgds-mapped-color-text-secondary);
    @include mixins.typography('paragraph-regular');
  }

  &__footer {
    display: flex;
    gap: var(--nxgds-alias-spacing-sm);
    width: 100%;
  }
}
</style>
